import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';
import PanelCard from '../../components/ui/PanelCard';
import AlertMessage from '../../components/ui/AlertMessage';
import { WarningIcon } from '../../components/ui/Icons';

/**
 * Staff › Settings (csak owner): a közösségi route-könyvtár ki/be kapcsolója (backend: site_settings.route_library).
 * 2026-10-07-ig a Codex owner-paneljén volt; a bot állapota (working / updating) és a globális fagyasztás ott maradt,
 * mert az előfizetéseket érinti.
 */

function SystemPill({ on, onLabel, offLabel }) {
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${
      on ? 'bg-brand-500/12 border-brand-400/25 text-brand-200' : 'bg-white/[0.05] border-white/[0.08] text-gray-500'
    }`}>
      <span className={`w-2 h-2 rounded-full ${on ? 'bg-brand-400' : 'bg-gray-600'}`} />
      {on ? onLabel : offLabel}
    </div>
  );
}

export default function LibrarySwitchCard() {
  const { toast } = useToast();
  const [enabled, setEnabled] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get('/owner/route-library')
      .then(res => setEnabled(!!res.data.enabled))
      .catch(() => setMsg({ type: 'error', text: 'Failed to load the route library setting.' }));
  }, []);

  const toggle = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.post('/owner/route-library', { enabled: !enabled });
      setEnabled(!!res.data.enabled);
      toast.success(res.data.message);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Error toggling the route library.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[560px]">
      <AlertMessage msg={msg} />
      <PanelCard
        title="Route Library"
        subtitle="The community route library. Testers, admins and the owner approve what goes in (Dashboard → Library review)."
      >
        {enabled === null ? <div className="skeleton h-9 w-48" /> : (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <SystemPill on={enabled} onLabel="Open" offLabel="Closed" />
              <button onClick={toggle} disabled={busy}
                className={enabled ? 'btn-danger py-1.5 px-3.5' : 'btn-outline py-1.5 px-3.5 text-sm'}>
                {busy ? '…' : enabled ? 'Close' : 'Open'}
              </button>
            </div>
            {!enabled && (
              <p className="text-xs text-amber-400/85 mt-3.5 flex items-start gap-1.5">
                <WarningIcon className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                Nobody can browse, add or submit. Listings stay stored, everyone keeps their own routes, and reviews still work.
              </p>
            )}
          </>
        )}
      </PanelCard>
    </div>
  );
}
